import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { CustomModal } from '../components/Modal'
import { Close, Home, Image, WaterDrop, ArrowForward } from '@mui/icons-material'
import { useLecturas } from '../hooks/useLecturas'
import { IHistoricoLecturasAgua } from '../models/lecturas'

const fmt = (n: number, decimals = 2) =>
    Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })

export const HistoricoLecturaAgua = () => {

    const navigate = useNavigate()

    const { getHistorico } = useLecturas()

    const [history, setHistory] = useState<IHistoricoLecturasAgua[]>([])
    const [loading, setLoading] = useState(true)
    const [openModal, setOpenModal] = useState(false)
    const [modalMessage, setModalMessage] = useState('')

    const [foto, setFoto] = useState<{ open: boolean, url: string, fecha: string }>({ open: false, url: '', fecha: '' })
    const [fotoError, setFotoError] = useState(false)

    const handleOpen = () => setOpenModal(true)
    const handleClose = () => {
        setOpenModal(false)
        navigate('/home')
    }

    const handleOpenFoto = (item: IHistoricoLecturasAgua) => {
        setFotoError(false)
        setFoto({ open: true, url: item.foto!, fecha: item.fecha })
    }
    const handleCloseFoto = () => setFoto(prev => ({ ...prev, open: false }))

    useEffect(() => {
        (async () => {
            const data = await getHistorico()
            if (data) {
                setHistory(data)
            }
            else {
                setModalMessage('Error al obtener el historial')
                handleOpen()
            }
            setLoading(false)
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const contador = history.length > 0 ? history[0].contador : ''

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f9', pb: 4 }}>

            <Box sx={{
                background: 'linear-gradient(135deg, #013467 0%, #0a4a8a 100%)',
                px: 3, py: 4, mb: 3,
            }}>
                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                    Consumo de Agua
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    Historial de lecturas de su contador
                </Typography>
                {contador && (
                    <Chip
                        icon={<WaterDrop sx={{ fontSize: '16px !important', color: '#013467 !important' }} />}
                        label={`No. Contador: ${contador}`}
                        size="small"
                        sx={{ mt: 1.5, backgroundColor: '#FFCC00', color: '#013467', fontWeight: 600 }}
                    />
                )}
            </Box>

            <Box sx={{ px: 2, maxWidth: 1000, mx: 'auto' }}>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && history.length === 0 && !openModal && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" mt={6}>
                        No hay lecturas registradas.
                    </Typography>
                )}

                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(290px, 1fr))' },
                    gap: 2,
                }}>
                    {!loading && history.map((item, index) => {
                        const hayFoto = !!item.foto && item.foto.trim() !== ''
                        const hayExceso = item.valor_Exceso > 0

                        return (
                            <Box
                                key={index}
                                sx={{
                                    backgroundColor: '#fff',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 8px rgba(1,52,103,0.08)',
                                }}
                            >
                                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #013467, #FFCC00)' }} />

                                <Box sx={{ p: 2.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant="subtitle2" sx={{ color: '#013467', fontWeight: 700 }}>
                                            {item.fecha}
                                        </Typography>
                                        <Chip
                                            label={hayExceso ? `Exceso Q. ${fmt(item.valor_Exceso)}` : 'Sin exceso'}
                                            size="small"
                                            sx={{
                                                height: 26, fontSize: '0.875rem', fontWeight: 700,
                                                backgroundColor: hayExceso ? '#fdecea' : '#e8f5e9',
                                                color: hayExceso ? '#c62828' : '#2e7d32',
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1.5 }}>
                                        <Typography variant="h4" sx={{ color: '#013467', fontWeight: 700, lineHeight: 1 }}>
                                            {fmt(item.agua_Consumo)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#555' }}>
                                            m³ consumidos
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
                                        backgroundColor: '#f4f6f9', borderRadius: 1.5, py: 1, px: 1.5,
                                    }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Anterior</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(item.agua_Saldo_Anterior, 0)}</Typography>
                                        </Box>
                                        <ArrowForward sx={{ color: '#bbb', fontSize: 18 }} />
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Actual</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(item.agua_Saldo_Actual, 0)}</Typography>
                                        </Box>
                                    </Box>

                                    {hayFoto && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Image sx={{ fontSize: '16px !important' }} />}
                                            onClick={() => handleOpenFoto(item)}
                                            sx={{
                                                display: 'flex', mx: 'auto', mt: 1.5, textTransform: 'none', fontWeight: 600, fontSize: 12,
                                                color: '#013467', borderColor: '#013467', borderRadius: 1.5,
                                            }}
                                        >
                                            Ver foto
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            </Box>

            <Dialog open={foto.open} onClose={handleCloseFoto} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Foto de la lectura {foto.fecha}
                    <IconButton aria-label="cerrar" onClick={handleCloseFoto} size="small">
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}
                >
                    {fotoError ? (
                        <Typography variant="body2" color="text.secondary">
                            No se pudo cargar la foto.
                        </Typography>
                    ) : (
                        <img
                            key={foto.url}
                            src={foto.url}
                            alt={'Lectura ' + foto.fecha}
                            style={{ maxWidth: '100%', maxHeight: 420, borderRadius: 8 }}
                            onError={() => setFotoError(true)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <CustomModal
                openState={openModal}
                onCloseHandler={handleClose}
            >
                <Typography variant="h5" component="div" gutterBottom marginY='1em'>
                    {modalMessage}
                </Typography>

                <Button color='secondary' variant="outlined" endIcon={<Home />} sx={{ marginY: 2, width: '50%' }} onClick={handleClose} >
                    Inicio
                </Button>

            </CustomModal>

        </Box>
    )
}
